import { ApolloClient, ApolloLink, from, InMemoryCache } from '@apollo/client';
import { SentryLink } from 'apollo-link-sentry';
import { TokenRefreshLink } from 'apollo-link-token-refresh';
import { createUploadLink } from 'apollo-upload-client';
import { onError } from '@apollo/client/link/error';
import { relayStylePagination } from '@apollo/client/utilities';
import { setContext } from '@apollo/client/link/context';

import O11y from './o11y';
import { AUTH_TOKEN_KEY, MANAGING_PROFILE_ID_KEY, REFRESH_TOKEN_KEY } from '#/constants/auth';
import { RefreshTokensPayload } from '#/__generated__/graphql/graphql';
import { StrictTypedTypePolicies } from '#/__generated__/apollo-helpers';
import { isTokenExpired } from './jwt';

const apiUrl = import.meta.env.VITE_GRAPHQL_SCHEMA_URL;

const authLink = setContext(async (req, prevCtx) => {
  const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
  if (!accessToken) {
    return;
  }
  return {
    headers: { ...prevCtx.headers, Authorization: `Bearer ${JSON.parse(accessToken)}` },
  };
});

const managingProfileLink = setContext(async (req, prevCtx) => {
  const managingProfileId = localStorage.getItem(MANAGING_PROFILE_ID_KEY);
  if (!managingProfileId) {
    return;
  }
  return {
    headers: { ...prevCtx.headers, 'Plank-Profile-ID': JSON.parse(managingProfileId) },
  };
});

const tokenRefreshLink = new TokenRefreshLink({
  accessTokenField: 'accessToken',
  fetchAccessToken: async () => {
    const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!accessToken || !refreshToken) {
      throw new Error('No tokens found');
    }
    return fetch(apiUrl, {
      body: JSON.stringify({
        query: `
mutation RefreshTokens($input: RefreshTokensInput!) {
  refreshTokens(input: $input) {
    accessToken
    refreshToken
  }
}`,
        variables: {
          input: { refreshToken: JSON.parse(refreshToken) },
        },
      }),
      cache: 'no-cache',
      headers: new Headers({
        'Authorization': `Bearer ${JSON.parse(accessToken)}`,
        'Content-Type': 'application/json',
      }),
      method: 'POST',
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.errors) {
          throw new Error(res.errors[0].message);
        }
        return res.data.refreshTokens;
      });
  },
  handleFetch: (accessToken) => {
    localStorage.setItem(AUTH_TOKEN_KEY, `"${accessToken}"`);
    // Manual trigger event =)
    dispatchEvent(
      new StorageEvent('storage', {
        key: AUTH_TOKEN_KEY,
        newValue: `"${accessToken}"`,
        storageArea: localStorage,
      }),
    );
  },
  handleResponse: () => (payload: RefreshTokensPayload) => {
    localStorage.setItem(REFRESH_TOKEN_KEY, `"${payload.refreshToken}"`);
    return payload;
  },
  handleError: () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(MANAGING_PROFILE_ID_KEY);
    // Manual trigger event =)
    dispatchEvent(
      new StorageEvent('storage', {
        key: AUTH_TOKEN_KEY,
        newValue: null,
        storageArea: localStorage,
      }),
    );
    O11y.setUser(null);
  },
  isTokenValidOrUndefined: () => {
    const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
    return !accessToken || !isTokenExpired(JSON.parse(accessToken));
  },
});

// TODO: fix typing
const httpLink = createUploadLink({ uri: apiUrl }) as unknown as ApolloLink;

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ extensions }) => {
      if (extensions.code === 'FORBIDDEN') {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(MANAGING_PROFILE_ID_KEY);
        // Manual trigger event =)
        dispatchEvent(
          new StorageEvent('storage', {
            key: AUTH_TOKEN_KEY,
            newValue: null,
            storageArea: localStorage,
          }),
        );
      }
    });
  }
});

const sentryLink = new SentryLink({ attachBreadcrumbs: { includeQuery: true } });

const makeRelayStylePaginationFields = () => {
  return ['customers', 'logistics', 'orders', 'users'].reduce(
    (fields, key) => ({
      ...fields,
      [key]: relayStylePagination(),
    }),
    {},
  );
};
// FIXME: adding this will have cache issue =(
const typePolicies: StrictTypedTypePolicies = {
  Query: {
    fields: makeRelayStylePaginationFields(),
  },
};
const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: from([authLink, tokenRefreshLink, managingProfileLink, sentryLink, httpLink]),
});

export default client;
